import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../hooks/useWallet';
import { useAuth } from '../hooks/useAuth';
import { useWalletContext } from '../context/WalletContext';
import { logger} from '../utils/logger';

// Cooldown period in hours
const WITHDRAWAL_COOLDOWN_HOURS = 24;

// Development-only logger
const devLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    logger.log(...args);
  }
};

// Development-only error logger
const devError = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(...args);
  }
};
import { useUser } from '@civic/auth-web3/react';
import { PublicKey, Connection, VersionedTransaction, Transaction, TransactionInstruction, SystemProgram, LAMPORTS_PER_SOL, Keypair, Message, TransactionMessage, SendTransactionError } from '@solana/web3.js';
import { failoverConnection as rpc } from '../utils/failoverConnection';
import { safeToBase58 } from './safeToBase58';
import { TOKEN_PROGRAM_ID, getAssociatedTokenAddress } from '@solana/spl-token';
import Decimal from 'decimal.js';
import { CONFIG } from '../config';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { recordDeposit, recordWithdrawal } from '../utils/transactionDb';
import styles from './RegularPool.module.css';
import Spinner from './Spinner';

// Constants
// Use centralized failover connection via 'rpc'
const USDC_MINT = new PublicKey('EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v');

// Cache constants
const CACHE_KEY = 'lulo_pools_data';
const CACHE_TIMESTAMP_KEY = 'lulo_pools_timestamp';
const CACHE_DURATION = 20 * 60 * 1000; // 20 minutes in milliseconds

function RegularPool() {
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [withdrawalModal, setWithdrawalModal] = useState(false);
  const [completeWithdrawalModal, setCompleteWithdrawalModal] = useState(false);
  const [pendingWithdrawalId, setPendingWithdrawalId] = useState('');
  const [activeTab, setActiveTab] = useState('deposit'); // 'deposit' or 'withdraw'
  const [metrics, setMetrics] = useState({
    totalLiquidity: 0,
    availableLiquidity: 0,
    regularLiquidityAmount: 0,
  });
  const [rates, setRates] = useState({
    CURRENT: 0,
    '1HR': 0,
    '24HR': 0,
    '7DAY': 0,
    '30DAY': 0,
    '1YR': 0
  });
  // Removed SOL balance check for first-time deposits
  const navigate = useNavigate();
  
  // Wallet and authentication
  const { wallet, walletAddress } = useWalletContext();
  const { balances, isLoading: walletLoading } = useWallet();
  const { email } = useAuth();
  const userContext = useUser();
  const civicWallet = userContext?.solana?.wallet;

  // Function to fetch data from API
  const fetchPoolsData = async () => {
    try {
      const options = {
        method: 'GET',
        headers: {
          'x-api-key': CONFIG.LULO_API_KEY,
          'Content-Type': 'application/json',
        },
      };
      const response = await fetch('https://api.lulo.fi/v1/pool.getPools', options);
      const data = await response.json();

      // Extract metrics from the response
      const metrics = {
        totalLiquidity: data.totalLiquidity || 0,
        availableLiquidity: data.availableLiquidity || 0,
        regularLiquidityAmount: data.regularLiquidityAmount || 0
      };
      setMetrics(metrics);

      let poolsList = [];
      if (Array.isArray(data.pools)) {
        poolsList = data.pools;
      } else if (Array.isArray(data.data)) {
        poolsList = data.data;
      } else if (data.regular && data.protected) {
        poolsList = [data.regular, data.protected];
      } else if (Array.isArray(data)) {
        poolsList = data;
      } else {
        throw new Error('Unexpected response format');
      }

      // Store in local storage
      const cacheData = {
        pools: poolsList,
        metrics,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());

      setPools(poolsList);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching pools data:', err);
      setError(err.message);
      setShowErrorToast(true);
      setLoading(false);
    }
  };

  // Function to load data from cache
  const loadFromCache = () => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (cachedData && cachedTimestamp) {
        const data = JSON.parse(cachedData);
        const timestamp = parseInt(cachedTimestamp);
        const now = Date.now();
        
        if (now - timestamp < CACHE_DURATION) {
          // Cache is still valid
          setPools(data.pools);
          setMetrics(data.metrics);
          setLoading(false);
          return true;
        }
      }
      return false;
    } catch (err) {
      console.error('Error loading from cache:', err);
      return false;
    }
  };

  // Fetch rates data
  const fetchRates = async () => {
    try {
      const response = await fetch('https://api.lulo.fi/v1/rates.getRates', {
        method: 'GET',
        headers: {
          'x-api-key': CONFIG.LULO_API_KEY,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch rates');
      
      const data = await response.json();
      if (data.regular) {
        setRates(data.regular);
      }
    } catch (err) {
      logger.error('Error fetching rates:', err);
    }
  };

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (!loadFromCache()) {
        await fetchPoolsData();
      }
      await fetchRates();
    };
    loadData();
  }, []);

  // Set up periodic refresh
  useEffect(() => {
    const refreshInterval = setInterval(async () => {
      await fetchPoolsData();
    }, CACHE_DURATION);

    return () => clearInterval(refreshInterval);
  }, []);

  // Fetch wallet balances
  useEffect(() => {
    async function fetchBalances() {
      if (!wallet?.publicKey) return;
      try {
        const ownerPubkey = wallet.publicKey;
        
        // Get USDC balance
        const resp = await rpc.getParsedTokenAccountsByOwner(ownerPubkey, { 
          programId: TOKEN_PROGRAM_ID 
        });
        let usdcAmt = 0;
        resp.value.forEach(({ account }) => {
          const info = account.data.parsed.info;
          const mint = info.mint;
          const amount = info.tokenAmount?.uiAmount || 0;
          if (mint === USDC_MINT.toBase58()) usdcAmt = amount;
        });
        
        // Log the USDC balance for debugging
        console.log('USDC Balance:', usdcAmt.toFixed(2));
      } catch (err) {
        console.error('Error fetching balances:', err);
      }
    }
    fetchBalances();
  }, [wallet?.publicKey]);

  // Toast close handlers
  const handleCloseErrorToast = () => setShowErrorToast(false);
  const handleCloseSuccessToast = () => setShowSuccessToast(false);
  const handleCloseToast = handleCloseErrorToast; // Alias for backward compatibility
  
  // Get the regular pool with null check
  const regularPool = (pools && pools.find(pool => pool.type === 'regular')) || {};

  // Function to get USDC balance
  const getUSDCBalance = async (walletAddress) => {
    try {
      const tokenAccounts = await rpc.getParsedTokenAccountsByOwner(
        walletAddress,
        { programId: TOKEN_PROGRAM_ID }
      );

      // Find USDC token account
      const usdcAccount = tokenAccounts.value.find(account => 
        account.account.data.parsed.info.mint === USDC_MINT.toBase58()
      );

      // If no USDC account, balance is 0
      if (!usdcAccount) return 0;

      // Return balance in USDC (6 decimals)
      return usdcAccount.account.data.parsed.info.tokenAmount.uiAmount;
    } catch (error) {
      console.error('Error fetching USDC balance:', error);
      return 0;
    }
  };

  const handleDeposit = async (pool, amount) => {
    if (!civicWallet?.publicKey || !wallet?.publicKey) {
      setError('Please connect your wallet first');
      setShowErrorToast(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Set minimum deposit amount (1.1 USDC)
      const MIN_DEPOSIT_AMOUNT = 1.1;
      if (amount < MIN_DEPOSIT_AMOUNT) {
        throw new Error(`Minimum deposit amount is $${MIN_DEPOSIT_AMOUNT}`);
      }

      // Calculate fee (0.5% of the deposit amount)
      const feeAmount = (amount * 0.5) / 100; // 0.5% fee
      const totalAmount = amount + feeAmount;
      const amountAfterFee = amount - feeAmount;

      // Check USDC balance before proceeding (convert to 6 decimals for comparison)
      const usdcBalance = await getUSDCBalance(wallet.publicKey);
      
      if (usdcBalance < totalAmount) {
        const shortBy = (totalAmount - usdcBalance).toFixed(6);
        throw new Error(`Insufficient USDC balance. You need ${shortBy} more USDC to complete this transaction.`);
      }

      const walletAddress = civicWallet.publicKey.toBase58();
      const userId = email || walletAddress; // Use email if available, otherwise use wallet address
      
      console.log(`User input: ${amount} USDC`);
      console.log(`Fee amount: ${feeAmount} USDC`);
      console.log(`Total amount (including fee): ${totalAmount} USDC`);
      console.log(`Amount after fee: ${amountAfterFee} USDC`);

      // Construct API request body with the exact user input amount
      const requestBody = {
        owner: walletAddress,
        feePayer: walletAddress,
        mintAddress: USDC_MINT.toBase58(),
        regularAmount: pool.type === 'regular' ? amount : 0,
        protectedAmount: 0
      };

      logger.log('Sending deposit request with actual USDC amount:', JSON.stringify({
        ...requestBody,
        headers: { 'Content-Type': 'application/json', 'x-api-key': '***' }
      }, null, 2));

      // Call Lulo API to generate the transaction
      const response = await fetch('https://api.lulo.fi/v1/generate.transactions.deposit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': CONFIG.LULO_API_KEY,
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Deposit API response status:', response.status);
      
      if (!response.ok) {
        // Try to extract JSON, fallback to text
        let errorBody = null;
        try { errorBody = await response.json(); } catch (_) { errorBody = await response.text(); }
        devError('Lulo API Error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorBody
        });
        const msg = (errorBody && errorBody.error && errorBody.error.message) ? errorBody.error.message : `Deposit API error: ${response.status} - ${response.statusText}`;
        throw new Error(msg);
      }

      const responseData = await response.json();
      const { transaction: txData } = responseData;
      
      // Log the full response from Lulo API
      logger.log('Lulo API Response:', JSON.stringify({
        instructions: responseData.instructions,
        transaction: '...base64 encoded transaction...',
        message: 'Transaction data has been parsed and will be used for deposit'
      }, null, 2));
      
      // Log detailed instructions if they exist
      if (responseData.instructions) {
        logger.log('Transaction Instructions:', {
        //  computeBudgetInstructions: responseData.instructions.computeBudgetInstructions?.length || 0,
         // regularDepositInstruction: responseData.instructions.regularDepositInstruction ? 'Present' : 'None',
        //  protectedDepositInstruction: responseData.instructions.protectedDepositInstruction ? 'Present' : 'None',
         // addressLookupTableAddresses: responseData.instructions.addressLookupTableAddresses || [],
        //  setupInstructions: responseData.instructions.setupInstructions?.length || 0
        });
      }
      
      // Deserialize the transaction
      const transactionBuffer = Buffer.from(txData, 'base64');
      const parsedTransaction = VersionedTransaction.deserialize(transactionBuffer);

      // Fetch ALT accounts if present to allow decompile/compile
      let addressLookupTableAccounts = [];
      try {
        if (parsedTransaction.message.addressTableLookups?.length) {
          const lookups = parsedTransaction.message.addressTableLookups;
          addressLookupTableAccounts = await Promise.all(
            lookups.map(async (l) => {
              const res = await rpc.getAddressLookupTable(new PublicKey(l.accountKey));
              return res.value;
            })
          );
          addressLookupTableAccounts = addressLookupTableAccounts.filter(Boolean);
        }
      } catch (altErr) {
        devError('Failed to fetch address lookup tables:', altErr);
      }

      // Rebuild with fresh blockhash to avoid "Blockhash not found"
      let { blockhash: latestBlockhash, lastValidBlockHeight } = await rpc.getLatestBlockhash('confirmed');
      const decompiled = TransactionMessage.decompile(parsedTransaction.message, { addressLookupTableAccounts });
      // Ensure payer remains the same
      if (!decompiled.payerKey) decompiled.payerKey = civicWallet.publicKey;
      decompiled.recentBlockhash = latestBlockhash;
      const v0msg = decompiled.compileToV0Message(addressLookupTableAccounts);
      const depositTx = new VersionedTransaction(v0msg);

      // Sign the transaction with Civic wallet
      logger.log('Signing transaction with Civic wallet...');
      logger.log('Transaction details:', {
        // numRequiredSignatures: depositTx.message.header.numRequiredSignatures,
        // numReadonlySignedAccounts: depositTx.message.header.numReadonlySignedAccounts,
        // numReadonlyUnsignedAccounts: depositTx.message.header.numReadonlyUnsignedAccounts,
        // accountKeys: depositTx.message.staticAccountKeys.map(k => k.toString())
      });
      
      let signedTx;
      let signature;
      
      try {
        // Sign with Civic wallet
        signedTx = await civicWallet.signTransaction(depositTx);
        console.log('✓ Transaction signed with Civic wallet');
        
        // Send the transaction
        signature = await rpc.sendRawTransaction(
          signedTx.serialize(),
          {
            skipPreflight: false,
            preflightCommitment: 'confirmed',
            maxRetries: 5
          }
        );
        logger.log('✓ Transaction sent with signature:', signature);
        
      } catch (error) {
        logger.error('Transaction failed:', error);
        // Enhance diagnostics for SendTransactionError
        try {
          if (error instanceof SendTransactionError && typeof error.getLogs === 'function') {
            const logs = await error.getLogs(rpc);
            devError('Transaction logs:', logs);
          }
        } catch (_) {}
        // If failure due to expired or blockhash missing, rebuild once and retry send
        const transient = /blockhash not found|block height exceeded|expired/i.test(error.message || '');
        if (transient) {
          try {
            ({ blockhash: latestBlockhash, lastValidBlockHeight } = await rpc.getLatestBlockhash('confirmed'));
            decompiled.recentBlockhash = latestBlockhash;
            const retryMsg = decompiled.compileToV0Message(addressLookupTableAccounts);
            const retryTx = new VersionedTransaction(retryMsg);
            const retrySigned = await civicWallet.signTransaction(retryTx);
            signature = await rpc.sendRawTransaction(retrySigned.serialize(), {
              skipPreflight: false,
              preflightCommitment: 'confirmed',
              maxRetries: 5
            });
            logger.log('✓ Resent transaction with signature:', signature);
          } catch (retrySendErr) {
            logger.error('Retry send failed:', retrySendErr);
            throw new Error(`Failed to send transaction: ${error.message}`);
          }
        } else {
          throw new Error(`Failed to send transaction: ${error.message}`);
        }
      }
      
      if (!signature) {
        throw new Error('Failed to send transaction: No signature generated');
      }
      
      logger.log('Transaction sent with signature:', signature);
      
      // Wait for confirmation with retry logic
      logger.log('Waiting for transaction confirmation...');
      let confirmation;
      const maxRetries = 10;
      let retryCount = 0;
      
      while (retryCount < maxRetries) {
        try {
          confirmation = await rpc.confirmTransaction({
            signature,
            blockhash: latestBlockhash,
            lastValidBlockHeight
          }, 'confirmed');
          
          if (confirmation.value.err) {
            throw new Error('Transaction failed');
          }
          
          logger.log('✓ Transaction confirmed:', confirmation);
          break;
        } catch (confirmationError) {
          const expired = /block height exceeded|expired/i.test(confirmationError.message || '');
          if (expired) {
            logger.log('Signature expired during confirmation. Rebuilding and re-sending...');
            try {
              ({ blockhash: latestBlockhash, lastValidBlockHeight } = await rpc.getLatestBlockhash('confirmed'));
              decompiled.recentBlockhash = latestBlockhash;
              const retryMsg = decompiled.compileToV0Message(addressLookupTableAccounts);
              const retryTx = new VersionedTransaction(retryMsg);
              const retrySigned = await civicWallet.signTransaction(retryTx);
              signature = await rpc.sendRawTransaction(retrySigned.serialize(), {
                skipPreflight: false,
                preflightCommitment: 'confirmed',
                maxRetries: 5
              });
              // do not increment retryCount; immediately loop to confirm the new signature
              continue;
            } catch (retryErr) {
              logger.error('Rebuild-and-resend failed:', retryErr);
              // fall-through to standard retry handling
            }
          }
          retryCount++;
          if (retryCount >= maxRetries) {
            logger.error('Max retries reached for transaction confirmation');
            throw new Error(`Transaction confirmation failed after ${maxRetries} attempts: ${confirmationError.message}`);
          }
          
          logger.log(`Retrying confirmation (${retryCount}/${maxRetries})...`);
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
        }
      }

      // Record the deposit in our database via API
      try {
        await fetch('/api/savings/deposit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            walletAddress,
            poolType: pool.type,
            amount: depositAmount,
            transactionHash: signature
          })
        });
        console.log('Deposit recorded in database');
      } catch (dbError) {
        console.error('Error recording deposit in database:', dbError);
        // Don't fail the deposit if database recording fails
      }

      // Show success message and refresh data
      logger.log('Deposit successful:', signature);
      setSuccessMessage(`Successfully deposited ${amount} USDC into ${pool.type} pool.\nTx Signature: ${signature}`);
      setShowSuccessToast(true);
      
      // Close the deposit modal
      setShowDepositModal(false);
      
      // Refresh pool data
      await fetchPoolsData();
      await fetchRates();
      
    } catch (error) {
      logger.error('Error in handleDeposit:', error);
      setError(error.message || 'Failed to process deposit');
      setShowErrorToast(true);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (pool, amount) => {
    if (!civicWallet?.publicKey) {
      setError('Please connect your wallet first');
      setShowErrorToast(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use the exact amount without conversion to lamports
      const withdrawAmount = amount;
      logger.log('Initiating withdrawal of', withdrawAmount, 'USDC from regular pool');

      // Call the Lulo API to generate withdrawal instructions
      const response = await fetch('https://api.lulo.fi/v1/generate.instructions.initiateRegularWithdraw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': CONFIG.LULO_API_KEY
        },
        body: JSON.stringify({
          owner: civicWallet.publicKey.toBase58(),
          feePayer: civicWallet.publicKey.toBase58(),
          mintAddress: USDC_MINT.toBase58(),
          amount: withdrawAmount
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        devError('Lulo API Error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(errorData.error?.message || 'Failed to generate withdrawal transaction');
      }
      
      const responseData = await response.json();
      devLog('Received response from Lulo API:', responseData);
      
      if (!responseData || !responseData.instructions) {
        console.error('Invalid response format - missing instructions:', responseData);
        throw new Error('Failed to generate withdrawal transaction: Invalid response format from server');
      }

      logger.log('Creating transaction from instructions...');
      try {
        // Create a new transaction
        const transaction = new Transaction();
        
        // Add all setup instructions
        if (responseData.instructions.setupInstructions && responseData.instructions.setupInstructions.length > 0) {
          logger.log('Adding setup instructions:', responseData.instructions.setupInstructions.length);
          transaction.add(...responseData.instructions.setupInstructions);
        }
        
        // Add compute budget instructions if they exist
        if (responseData.instructions.computeBudgetInstructions && responseData.instructions.computeBudgetInstructions.length > 0) {
          logger.log('Adding compute budget instructions:', responseData.instructions.computeBudgetInstructions.length);
          transaction.add(...responseData.instructions.computeBudgetInstructions);
        }
        
        // Add the main withdrawal instruction
        if (responseData.instructions.initiateRegularWithdrawInstruction) {
          logger.log('Adding withdrawal instruction');
          const { keys, programId, data } = responseData.instructions.initiateRegularWithdrawInstruction;
          const instruction = new TransactionInstruction({
            keys: keys.map(k => ({
              pubkey: new PublicKey(k.pubkey),
              isSigner: k.isSigner,
              isWritable: k.isWritable
            })),
            programId: new PublicKey(programId),
            data: Buffer.from(data)
          });
          transaction.add(instruction);
        }
        
        // Get recent blockhash and fee payer
        const { blockhash } = await rpc.getRecentBlockhash();
        transaction.recentBlockhash = blockhash;
        transaction.feePayer = civicWallet.publicKey;
        
        // Sign the transaction with the user's wallet (Civic wallet)
        logger.log('Signing transaction with user wallet...');
        const signedTx = await civicWallet.signTransaction(transaction);
        
        // Send the transaction with retry logic
        logger.log('Sending transaction...');
        const rawTransaction = signedTx.serialize();
        const signature = await rpc.sendRawTransaction(rawTransaction, {
          skipPreflight: false,
          preflightCommitment: 'confirmed',
          maxRetries: 3
        });
        
        // Wait for confirmation
        logger.log('Waiting for transaction confirmation...');
        const confirmation = await rpc.confirmTransaction({
          signature,
          blockhash: transaction.recentBlockhash,
          lastValidBlockHeight: await rpc.getBlockHeight() + 150 // 150 blocks is about 5 minutes
        }, 'confirmed');

        if (confirmation.value.err) {
          logger.error('Transaction failed:', confirmation.value.err);
          throw new Error('Transaction failed: ' + JSON.stringify(confirmation.value.err));
        }

        // Show success message
        logger.log('Withdrawal successful:', signature);
        const formattedAmount = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount);
        
        setSuccessMessage({
          title: 'Withdrawal Successful!',
          message: `${formattedAmount} USDC has been successfully withdrawn from your regular pool.`,
          txHash: signature
        });
        setShowSuccessToast(true);
        setWithdrawalModal(false);
        setWithdrawalAmount(''); // Reset withdrawal amount
        
        // Refresh pool data
        fetchPoolsData();
        
        // Record the withdrawal in our database via API
        try {
          await fetch('/api/savings/withdraw', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: email || civicWallet.publicKey.toBase58(),
            walletAddress: civicWallet.publicKey.toBase58(),
            poolType: 'regular',
            amount: withdrawAmount,
            transactionHash: signature
          })
        });
        logger.log('Withdrawal recorded in database');
      } catch (dbError) {
        logger.error('Error recording withdrawal in database:', dbError);
        // Don't fail the withdrawal if database recording fails
      }

      // Success message is already set and toast is shown
      // Refresh pool data after a short delay to allow the user to see the success message
      setTimeout(() => {
        fetchPoolsData();
      }, 2000);
      } catch (innerErr) {
        console.error('Error in withdrawal transaction:', innerErr);
        throw innerErr; // Re-throw to be caught by the outer catch
      }
    } catch (err) {
      console.error('Lulo withdrawal error:', err);
      setError(err.message);
      setShowErrorToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteWithdrawal = async () => {
    if (!civicWallet) {
      setError('Wallet not connected');
      setShowErrorToast(true);
      return;
    }

    // Safely get the public key from the wallet
    const publicKey = civicWallet.publicKey || 
                     (civicWallet.wallet?.publicKey) || 
                     (civicWallet.wallet?.publicKey?.toBase58 && civicWallet.wallet.publicKey) ||
                     null;

    if (!publicKey) {
      setError('Could not get public key from wallet');
      setShowErrorToast(true);
      return;
    }

    const publicKeyString = typeof publicKey === 'string' ? publicKey : publicKey.toBase58();

    if (!pendingWithdrawalId) {
      setError('Please enter a valid withdrawal ID');
      setShowErrorToast(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      devLog('Completing withdrawal with ID:', pendingWithdrawalId);

      // Call the Lulo API to complete the withdrawal
      const response = await fetch('https://api.lulo.fi/v1/generate.instructions.completeRegularWithdrawal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': CONFIG.LULO_API_KEY
        },
        body: JSON.stringify({
          owner: publicKeyString,
          pendingWithdrawalId: parseInt(pendingWithdrawalId, 10),
          feePayer: publicKeyString
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        devError('Lulo API Error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(errorData.error?.message || `Failed to complete withdrawal: ${response.status} ${response.statusText}`);
      }

      const responseData = await response.json();
      devLog('Received response from Lulo API:', responseData);

      if (!responseData?.instructions?.completeRegularWithdrawInstruction) {
        throw new Error('Invalid response format from server: Missing completeRegularWithdrawInstruction');
      }

      // Create transaction
      const transaction = new Transaction();
      
      // Add compute budget instructions if present
      if (responseData.instructions.computeBudgetInstructions?.length > 0) {
        responseData.instructions.computeBudgetInstructions.forEach(ix => {
          transaction.add(new TransactionInstruction({
            programId: new PublicKey(ix.programId),
            keys: ix.keys.map(k => ({
              pubkey: new PublicKey(k.pubkey),
              isSigner: k.isSigner,
              isWritable: k.isWritable
            })),
            data: Buffer.from(ix.data, 'base64')
          }));
        });
      }
      
      // Add setup instructions if present
      if (responseData.instructions.setupInstructions?.length > 0) {
        responseData.instructions.setupInstructions.forEach(ix => {
          transaction.add(new TransactionInstruction({
            programId: new PublicKey(ix.programId),
            keys: ix.keys.map(k => ({
              pubkey: new PublicKey(k.pubkey),
              isSigner: k.isSigner,
              isWritable: k.isWritable
            })),
            data: Buffer.from(ix.data, 'base64')
          }));
        });
      }
      
      // Add the main complete withdrawal instruction
      const { completeRegularWithdrawInstruction } = responseData.instructions;
      const instruction = new TransactionInstruction({
        programId: new PublicKey(completeRegularWithdrawInstruction.programId),
        keys: completeRegularWithdrawInstruction.keys.map(k => ({
          pubkey: new PublicKey(k.pubkey),
          isSigner: k.isSigner,
          isWritable: k.isWritable
        })),
        data: Buffer.from(completeRegularWithdrawInstruction.data, 'base64')
      });
      transaction.add(instruction);

      // Set recent blockhash and fee payer
      const { blockhash } = await rpc.getLatestBlockhash('confirmed');
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = typeof publicKey === 'string' ? new PublicKey(publicKey) : publicKey;

      // Sign transaction with the wallet
      devLog('Signing transaction...');
      let signedTx;
      
      try {
        // Try signTransaction first
        if (typeof civicWallet.signTransaction === 'function') {
          signedTx = await civicWallet.signTransaction(transaction);
        } 
        // Fallback to sendTransaction if available
        else if (typeof civicWallet.sendTransaction === 'function') {
          return await civicWallet.sendTransaction(transaction, connection, {
            skipPreflight: false,
            preflightCommitment: 'confirmed',
            maxRetries: 3
          });
        } else {
          throw new Error('No supported transaction signing method found');
        }
      } catch (signError) {
        devError('Signing error:', signError);
        throw new Error(`Failed to sign transaction: ${signError.message}`);
      }

      // Define signature in the outer scope
      let signature;
      
      try {
        // Send the signed transaction
        console.log('Sending transaction...');
        signature = await rpc.sendRawTransaction(signedTx.serialize(), {
          skipPreflight: false,
          preflightCommitment: 'confirmed',
          maxRetries: 3
        });

        // Wait for confirmation
        devLog('Waiting for confirmation...');
        const confirmation = await rpc.confirmTransaction({
          signature,
          blockhash,
          lastValidBlockHeight: (await rpc.getBlockHeight()) + 150
        }, 'confirmed');

        if (confirmation.value.err) {
          throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`);
        }
      } catch (err) {
        devError('Transaction error:', err);
        if ((err.logs && err.logs.some(log => log.includes('CooldownNotPassed'))) || 
            err.message.includes('0x17a9')) {
          throw new Error(
            `Withdrawal cooldown period (${WITHDRAWAL_COOLDOWN_HOURS} hours) has not passed yet.\n\n` +
            'For security reasons, you must wait after initiating a withdrawal before you can complete it.\n' +
            'Please try again later.'
          );
        }
        throw err;
      }

      // Update UI on success
      setSuccessMessage({
        title: 'Withdrawal Completed!',
        message: 'Your withdrawal has been successfully completed.',
        txHash: signature
      });
      setShowSuccessToast(true);
      setCompleteWithdrawalModal(false);
      setPendingWithdrawalId('');
      
      // Refresh pool data
      await fetchPoolsData();
      
    } catch (err) {
      devError('Complete withdrawal error:', err);
      setError(err.message || 'Failed to complete withdrawal');
      setShowErrorToast(true);
    } finally {
      setLoading(false);
    }
  };

  // Format wallet address for display
  const formatAddress = (address) => {
    if (!address) return 'Not connected';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };
  
  // --- Formatting helpers ---
  function formatAPY(apy) {
    if (apy === undefined || apy === null) return '-';
    return (Number(apy) * 100).toFixed(2) + '%';
  }

  function formatUSDC(amount) {
    if (amount === undefined || amount === null) return '-';
    return Number(amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' USDC';
  }

  return (
    <div className="pools-container" style={{ padding: '0 1rem 2rem' }}>
      {/* Success Toast */}
      {showSuccessToast && successMessage && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          backgroundColor: '#10B981',
          color: 'white',
          padding: '16px 20px',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          maxWidth: '90%',
          width: '400px',
          animation: 'fadeIn 0.3s ease-out',
          borderLeft: '4px solid #059669',
          overflow: 'hidden',
          '@media (max-width: 480px)': {
            width: '90%',
            padding: '14px 16px',
            maxWidth: 'calc(100% - 32px)'
          }
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', width: '100%' }}>
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '2px'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontWeight: 600, 
                marginBottom: '4px',
                fontSize: '1.05em'
              }}>
                {successMessage.title || 'Success!'}
              </div>
              <div style={{ 
                fontSize: '0.95em', 
                opacity: 0.9,
                lineHeight: 1.4
              }}>
                {successMessage.message}
              </div>
              {successMessage.txHash && (
                <div style={{ 
                  marginTop: '8px',
                  fontSize: '0.85em',
                  opacity: 0.8,
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  lineHeight: '1.4'
                }}>
                  TX: {successMessage.txHash}
                </div>
              )}
            </div>
            <button 
              onClick={() => setShowSuccessToast(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                opacity: 0.7,
                padding: '2px',
                borderRadius: '4px',
                transition: 'all 0.2s',
                alignSelf: 'flex-start',
                ':hover': {
                  opacity: 1,
                  background: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #10B981, #34D399)'
          }} />
        </div>
      )}

      {/* Error Toast */}
      {showErrorToast && error && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          backgroundColor: '#EF4444',
          color: 'white',
          padding: '16px 20px',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          maxWidth: '90%',
          width: '400px',
          animation: 'fadeIn 0.3s ease-out',
          borderLeft: '4px solid #DC2626',
          overflow: 'hidden',
          '@media (max-width: 480px)': {
            width: '90%',
            padding: '14px 16px',
            maxWidth: 'calc(100% - 32px)'
          }
        }}>
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: '2px'
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9V12M12 15H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontWeight: 600, 
              marginBottom: '2px',
              fontSize: '0.95em'
            }}>
              Error
            </div>
            <div style={{ 
              fontSize: '0.9em', 
              opacity: 0.9,
              lineHeight: 1.3
            }}>
              {error}
            </div>
          </div>
          <button 
            onClick={() => setShowErrorToast(false)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              opacity: 0.7,
              padding: '2px',
              borderRadius: '4px',
              transition: 'all 0.2s',
              alignSelf: 'flex-start',
              ':hover': {
                opacity: 1,
                background: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #EF4444, #F87171)'
          }} />
        </div>
      )}
      
      {/* Header Section */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '1.5rem 0',
        gap: '1rem',
        borderBottom: '1px solid #e5e9f2',
        marginBottom: '1.5rem'
      }}>
        <button 
          className="action-btn" 
          onClick={() => navigate('/pools')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: '#f3f4f6',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.95rem',
            fontWeight: 500
          }}
        >
          ← Back to Pools
        </button>
        
      </div>

      {/* Modern Pool Overview Section */}
      <div style={{
        maxWidth: '900px', 
        margin: '0 auto', 
        padding: '1.5rem', 
        background: '#fff', 
        borderRadius: '18px', 
        boxShadow: '0 2px 12px #1a2cff10', 
        border: '1.5px solid #e5e9f2',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <h1 style={{
          fontWeight: 800, 
          fontSize: '1.5rem',
          marginBottom: '0.75rem',
          lineHeight: '1.3'
        }}>
          Regular Pool
        </h1>
        <div style={{
          color: '#6b7280', 
          fontSize: '0.95rem', 
          marginBottom: '1.5rem',
          lineHeight: '1.5'
        }}>
          Standard USDC deposits earning yield with no risk protection.
        </div>

        {/* Pool Details Card */}
        <div style={{
          background: '#f8f9fa', 
          borderRadius: '12px', 
          padding: '1.25rem', 
          marginBottom: '1.5rem',
          overflow: 'hidden'
        }}>
          <h3 style={{
            fontWeight: 700, 
            fontSize: '1.1rem', 
            marginBottom: '1rem',
            color: '#1f2937'
          }}>
            Pool Details
          </h3>
          <div className={styles.poolDetailsGrid}>

            <div>
              <div style={{
                color: '#6b7280', 
                fontSize: '0.85rem', 
                marginBottom: '0.35rem',
                lineHeight: '1.4'
              }}>
                Pool Name
              </div>
              <div style={{
                fontWeight: 600, 
                fontSize: '1rem',
                color: '#111827'
              }}>
                Regular Pool
              </div>
            </div>
            <div>
              <div style={{
                color: '#6b7280', 
                fontSize: '0.85rem', 
                marginBottom: '0.35rem',
                lineHeight: '1.4'
              }}>
                APY
              </div>
              <div style={{
                fontWeight: 600, 
                fontSize: '1rem',
                color: '#111827'
              }}>
                {rates.CURRENT ? rates.CURRENT.toFixed(2) + '%' : '--'}
              </div>
            </div>
            <div>
              <div style={{
                color: '#6b7280', 
                fontSize: '0.85rem', 
                marginBottom: '0.35rem',
                lineHeight: '1.4'
              }}>
                Liquidity Amount
              </div>
              <div style={{
                fontWeight: 600, 
                fontSize: '1rem',
                color: '#111827',
                wordBreak: 'break-word'
              }}>
                {formatUSDC(metrics.regularLiquidityAmount)}
              </div>
            </div>
            <div>
              <div style={{
                color: '#6b7280', 
                fontSize: '0.85rem', 
                marginBottom: '0.35rem',
                lineHeight: '1.4'
              }}>
                Min Deposit
              </div>
              <div style={{
                fontWeight: 600, 
                fontSize: '1rem',
                color: '#111827'
              }}>
                1.04 USDC
              </div>
            </div>
          </div>
        </div>

        <div style={{
          fontWeight: 700, 
          fontSize: '1.1rem', 
          margin: '1.5rem 0 1rem',
          color: '#1f2937'
        }}>
          Performance Metrics
        </div>
        <div style={{
          background: '#f8faff', 
          border: '1.5px solid #e5e9f2', 
          borderRadius: '12px', 
          padding: '1.25rem', 
          marginBottom: '1.5rem',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: '1.25rem',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            <div>
              <div style={{
                fontSize: '1.5rem', 
                fontWeight: 800, 
                marginBottom: '0.25rem',
                lineHeight: '1.3',
                color: '#111827'
              }}>
                {rates.CURRENT ? rates.CURRENT.toFixed(2) + '%' : '--'}
              </div>
              <div style={{
                color: '#16c784', 
                fontWeight: 600, 
                fontSize: '0.9rem',
                lineHeight: '1.4'
              }}>
                {rates['1YR'] && rates.CURRENT ? 
                  `Last 12 Months: ${(rates['1YR'] - rates.CURRENT) > 0 ? '+' : ''}${(rates['1YR'] - rates.CURRENT).toFixed(2)}%` : ''
                }
              </div>
            </div>
          </div>
          <div style={{
            height: '280px', 
            width: '100%',
            minWidth: '280px',
            position: 'relative',
            margin: '0 auto'
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { name: '1Y', value: rates['1YR'] },
                  { name: '30D', value: rates['30DAY'] },
                  { name: '7D', value: rates['7DAY'] },
                  { name: '24H', value: rates['24HR'] },
                  { name: '1H', value: rates['1HR'] },
                  { name: 'Now', value: rates['CURRENT'] },
                ]}
                margin={{ 
                  top: 10, 
                  right: 10, 
                  left: 0, 
                  bottom: 10 
                }}
              >
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  vertical={false} 
                  stroke="#e5e9f2" 
                />
                <XAxis 
                  dataKey="name" 
                  axisLine={false} 
                  tickLine={false}
                  tick={{ 
                    fill: '#6b7280', 
                    fontSize: '0.8rem',
                    '@media (max-width: 480px)': {
                      fontSize: '0.7rem'
                    }
                  }}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false}
                  tick={{ 
                    fill: '#6b7280', 
                    fontSize: '0.8rem',
                    '@media (max-width: 480px)': {
                      fontSize: '0.7rem'
                    }
                  }}
                  tickFormatter={v => v ? `${v.toFixed(2)}%` : ''}
                  domain={[0, dataMax => Math.max(10, dataMax * 1.2)]}
                  width={40}
                />
                <Tooltip 
                  formatter={v => v ? `${v.toFixed(2)}%` : ''}
                  labelFormatter={label => `Timeframe: ${label}`}
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #e5e9f2',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    padding: '8px 12px',
                    fontSize: '0.9rem',
                    '@media (max-width: 480px)': {
                      fontSize: '0.8rem',
                      padding: '6px 10px'
                    }
                  }}
                />
                <Bar 
                  dataKey="value" 
                  fill="#1a2cff"
                  radius={[4, 4, 0, 0]}
                  barSize={30}
                >
                  {[
                    { name: '1Y', value: rates['1YR'] },
                    { name: '30D', value: rates['30DAY'] },
                    { name: '7D', value: rates['7DAY'] },
                    { name: '24H', value: rates['24HR'] },
                    { name: '1H', value: rates['1HR'] },
                    { name: 'Now', value: rates['CURRENT'] },
                  ].map((entry, index) => (
                    <text
                      key={index}
                      x={index * (100 / 6) + (100 / 12) + '%'}
                      y={entry.value ? 'calc(100% - 20px)' : '100%'}
                      fill="#1a2cff"
                      textAnchor="middle"
                      style={{ 
                        fontSize: '0.7rem', 
                        fontWeight: 600,
                        pointerEvents: 'none',
                        '@media (max-width: 480px)': {
                          fontSize: '0.6rem'
                        }
                      }}
                    >
                      {entry.value ? entry.value.toFixed(2) + '%' : ''}
                    </text>
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>



        {/* Deposit/Withdraw Cards Section */}
        <div style={{
          display: 'flex',
          gap: '1.5rem',
          justifyContent: 'center',
          alignItems: 'flex-start',
          marginBottom: '2rem',
          flexWrap: 'wrap'
        }}>
          {/* Balance Card */}
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 2px 12px #1a2cff10',
            border: '1.5px solid #e5e9f2',
            minWidth: 260,
            flex: '1 1 260px',
            padding: '2rem 1.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            maxWidth: 320,
            width: '100%'
          }}>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1f2937', marginBottom: '1rem' }}>Your Balance</div>
            <div style={{ color: '#2563eb', fontSize: '1rem', fontWeight: 500, marginBottom: 4, cursor: 'pointer' }}>USDC Balance</div>
            <div style={{ fontWeight: 700, fontSize: '1.35rem', color: '#111827', letterSpacing: '-0.5px', wordBreak: 'break-all' }}>
              {parseFloat(balances.USDC || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDC
            </div>
          </div>
          {/* Deposit Card */}
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 2px 12px #1a2cff10',
            border: '1.5px solid #e5e9f2',
            minWidth: 260,
            flex: '1 1 260px',
            padding: '2rem 1.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            maxWidth: 320,
            width: '100%'
          }}>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1f2937', marginBottom: '1rem' }}>Deposit USDC</div>
            <div style={{ width: '100%' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #e5e9f2',
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                marginBottom: '1rem',
                width: '100%',
                paddingRight: 0,
                paddingLeft: 0
              }}>
                <input
                  type="number"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '0.875rem 1rem',
                    fontSize: '1.05rem',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    minWidth: 0
                  }}
                />
                <span style={{ color: '#6b7280', fontWeight: 600, fontSize: '0.95rem', padding: '0 8px', whiteSpace: 'nowrap' }}>USDC</span>
                <button 
                  onClick={() => setDepositAmount(balances.USDC || '0')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2563eb',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    padding: '0 8px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                  }}
                >
                  MAX
                </button>
              </div>
              <button 
                onClick={() => handleDeposit(regularPool, Number(depositAmount))}
                disabled={loading || !depositAmount || Number(depositAmount) <= 0}
                style={{
                  width: '100%',
                  padding: '0.9rem 1rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  backgroundColor: (loading || !depositAmount || Number(depositAmount) <= 0) ? '#f3f5f7' : '#2563eb',
                  color: (loading || !depositAmount || Number(depositAmount) <= 0) ? '#b0b3bb' : '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (loading || !depositAmount || Number(depositAmount) <= 0) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  marginTop: 2
                }}
              >
                {loading ? 'Processing...' : 'Deposit'}
              </button>
              {false && (
                <div style={{
                  color: '#ef4444',
                  fontSize: '0.85rem',
                  marginTop: '0.75rem',
                  textAlign: 'center',
                  backgroundColor: '#fef2f2',
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  border: '1px solid #fecaca'
                }}>
                  First-time deposits require One-Time Initialization Fee: 0.005 SOL + transaction costs.
                  <br />
                  Your balance: {solBalance.toFixed(4)} SOL • You need {(0.05 - solBalance).toFixed(4)} more SOL
                </div>
              )}
            </div>
          </div>
          {/* Withdraw Card */}
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 2px 12px #1a2cff10',
            border: '1.5px solid #e5e9f2',
            minWidth: 260,
            flex: '1 1 260px',
            padding: '2rem 1.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            maxWidth: 320,
            width: '100%'
          }}>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1f2937', marginBottom: '1rem' }}>Withdraw USDC</div>
            <div style={{ width: '100%' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #e5e9f2',
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                marginBottom: '1rem',
                width: '100%',
                paddingRight: 0,
                paddingLeft: 0
              }}>
                <input
                  type="number"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  value={withdrawalAmount}
                  onChange={(e) => setWithdrawalAmount(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '0.875rem 1rem',
                    fontSize: '1.05rem',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    minWidth: 0
                  }}
                />
                <span style={{ color: '#6b7280', fontWeight: 600, fontSize: '0.95rem', padding: '0 8px', whiteSpace: 'nowrap' }}>USDC</span>
                <button 
                  onClick={() => setWithdrawalAmount(regularPool.maxWithdrawalAmount || '0')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2563eb',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    padding: '0 8px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                  }}
                >
                  MAX
                </button>
              </div>
              <button 
                onClick={() => handleWithdraw(regularPool, Number(withdrawalAmount))}
                disabled={loading || !withdrawalAmount || Number(withdrawalAmount) <= 0}
                style={{
                  width: '100%',
                  padding: '0.9rem 1rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  backgroundColor: loading || !withdrawalAmount || Number(withdrawalAmount) <= 0 ? '#d6cafd' : '#8b7cf6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading || !withdrawalAmount || Number(withdrawalAmount) <= 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  marginTop: 2,
                  marginBottom: '1rem'
                }}
              >
                {loading ? 'Processing...' : 'Withdraw'}
              </button>
              
              {/* Complete Withdrawal Button */}
              <button 
                onClick={() => setCompleteWithdrawalModal(true)}
                style={{
                  width: '100%',
                  padding: '0.9rem 1rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  backgroundColor: '#e0f2fe',
                  color: '#0369a1',
                  border: '1px solid #bae6fd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  ':hover': {
                    backgroundColor: '#bae6fd',
                    borderColor: '#7dd3fc'
                  }
                }}
              >
                Complete Withdrawal
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Complete Withdrawal Modal */}
      {completeWithdrawalModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '2rem',
            width: '100%',
            maxWidth: '450px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
            position: 'relative'
          }}>
            <button 
              onClick={() => {
                setCompleteWithdrawalModal(false);
                setPendingWithdrawalId('');
              }}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '0.25rem',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                ':hover': {
                  backgroundColor: '#f3f4f6'
                }
              }}
            >
              ×
            </button>
            
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '1.5rem',
              paddingRight: '2rem'
            }}>
              Complete Withdrawal
            </h2>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Pending Withdrawal ID
              </label>
              <input
                type="number"
                value={pendingWithdrawalId}
                onChange={(e) => setPendingWithdrawalId(e.target.value)}
                placeholder="Enter withdrawal ID"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  fontSize: '1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  ':focus': {
                    borderColor: '#8b7cf6',
                    boxShadow: '0 0 0 3px rgba(139, 124, 246, 0.1)'
                  }
                }}
              />
              <p style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                marginTop: '0.5rem'
              }}>
                Enter the withdrawal ID you received when you initiated the withdrawal.
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '2rem'
            }}>
              <button
                onClick={handleCompleteWithdrawal}
                disabled={loading || !pendingWithdrawalId}
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  backgroundColor: loading || !pendingWithdrawalId ? '#d1d5db' : '#8b7cf6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading || !pendingWithdrawalId ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  ':hover': !loading && pendingWithdrawalId ? {
                    backgroundColor: '#7c6cf1'
                  } : {}
                }}
              >
                {loading ? 'Processing...' : 'Complete Withdrawal'}
              </button>
              
              <button
                onClick={() => {
                  setCompleteWithdrawalModal(false);
                  setPendingWithdrawalId('');
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  backgroundColor: '#f3f4f6',
                  color: '#4b5563',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  ':hover': {
                    backgroundColor: '#e5e7eb'
                  }
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RegularPool;